import PropTypes from 'prop-types';
import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { TextField } from '@mui/material';
import { composeEvent } from '@icp/utils';
import { HELPER_TEXT_TYPES, helpersIsEmpty, toDecimalPrecision } from '@icp/form-renderer-core';
import { FormHelperTextMaterial } from '../../FormHelperText';

function isTypedNumber(value) {
  return (
    // 本身就是合法 number
    typeof value === 'number' ||
    // string 可以被转换成 number，其实 material 这里拿到的值有 input type number 限制，最差只能是空字符串，不会有其余字符，但是为了保险还是多判断一下
    (typeof value === 'string' && value !== '' && !Number.isNaN(Number(value)))
  );
}

function toNumber(value, stringMode) {
  if (!isTypedNumber(value)) {
    return null;
  }
  if (stringMode) {
    return value;
  }
  return Number(value);
}

const NumberPickerMaterial = forwardRef(function NumberPickerMaterial(props, ref) {
  const {
    id,
    title,
    value,
    disabled,
    readonly,
    componentProps,
    validation,
    status,
    helpers,
    onChange,
    onTouchChanged,
    onBlur,
    onKeyDown,
    onKeyUp,
    precision,
    readonlyText,
    // min, max, step, precision, formatOptions
  } = props;

  const { changeDelayToBlur, stringMode, ...otherComponentProps } = componentProps;

  const [tempValue, setTempValue] = useState(value);
  // 让 keydown 和 keyup 之间 onChange 只会触发一次，防止一致按住 Enter 键反复触发 onChange
  const once = useRef(false);

  useEffect(() => {
    setTempValue(value);
  }, [value]);

  const triggerChange = (newValue) => {
    // ant 支持 precision 属性，material ui 不支持，这里自定义支持让 material 行为和 ant 保持一致。
    const fixedValue = toDecimalPrecision(newValue, precision, stringMode);
    // 同时material ui 可以输入 e 和空字符串，是非法的 number，toDecimalPrecision 不处理 precision 不存在的情况，所以要用 toNumber 判断一下。
    const validValue = toNumber(fixedValue, stringMode);
    onChange(validValue);
    return validValue;
  };

  const handleChange = (event) => {
    const newValue = event.target.value;
    setTempValue(newValue);

    if (!changeDelayToBlur) {
      triggerChange(newValue);
    }
  };

  const handleBlur = (event) => {
    const newValue = event.target.value;
    setTempValue(newValue);

    let validValue;
    if (changeDelayToBlur) {
      validValue = triggerChange(newValue);
    } else {
      const fixedValue = toDecimalPrecision(newValue, precision, stringMode);
      validValue = toNumber(fixedValue, stringMode);
    }

    // 有 precision 的值在 blur 过后 value 和 tempValue 可能会不同，同时例如 1.2 和 1.23 处理 precision: 1
    // 过后都是 1.2，value 没有改变也没有触发 useEffect 的更新，这里手动修改 tempValue 为最新值
    setTempValue(validValue);

    if (onTouchChanged) {
      onTouchChanged();
    }
  };

  // 当 changeDelayToBlur 时，按回车键也需要触发 onChange。和原生 input 逻辑一致
  const handleKeyDown = (event) => {
    if (once.current) {
      return;
    }

    if (event.key === 'Enter') {
      once.current = true;
      handleBlur(event);
    }
  };

  const handleKeyUp = (event) => {
    if (event.key === 'Enter') {
      once.current = false;
    }
  };

  const InputProps = useMemo(() => {
    const obj = { readOnly: readonly };
    // 必须这么单独指定一下，obj 不能有 value 属性，undefined 都不行，否则都会让 TextField 的 value 值失效
    if (readonly) {
      obj.value = readonlyText;
    }
    return obj;
  }, [readonly, readonlyText]);

  return (
    <TextField
      // 当 readonly 的时候，需要在 InputProps.value 里直接指定显示文字，type 不能是 number，而是 string
      type={readonly ? 'text' : 'number'}
      {...otherComponentProps}
      fullWidth={true}
      id={id}
      label={title}
      value={tempValue ?? ''}
      inputMode="numeric"
      error={status === 'error'}
      helperText={!helpersIsEmpty(helpers) ? <FormHelperTextMaterial helpers={helpers} /> : null}
      required={validation?.required}
      disabled={disabled}
      InputProps={InputProps}
      onChange={handleChange}
      onBlur={composeEvent(handleBlur, onBlur)}
      onKeyDown={composeEvent(handleKeyDown, onKeyDown)}
      onKeyUp={composeEvent(handleKeyUp, onKeyUp)}
      ref={ref}
    />
  );
});

NumberPickerMaterial.propTypes = {
  id: PropTypes.string,
  title: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  componentProps: PropTypes.shape({
    changeDelayToBlur: PropTypes.bool,
    stringMode: PropTypes.bool,
  }),
  fieldTitleProps: PropTypes.shape({
    showColon: PropTypes.bool,
  }),
  validation: PropTypes.shape({
    required: PropTypes.bool,
  }),
  disabled: PropTypes.bool,
  readonly: PropTypes.bool,

  status: PropTypes.oneOf(HELPER_TEXT_TYPES),
  helpers: PropTypes.arrayOf(
    PropTypes.shape({
      status: PropTypes.oneOf(HELPER_TEXT_TYPES),
      text: PropTypes.string,
    }),
  ),
  onChange: PropTypes.func,
  onTouchChanged: PropTypes.func,
  onBlur: PropTypes.func,
  onKeyDown: PropTypes.func,
  onKeyUp: PropTypes.func,
  precision: PropTypes.number,
  readonlyText: PropTypes.string,
};

export default NumberPickerMaterial;
